import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import { orgApi, usersApi, apiKeysApi, billingApi } from '@/api/clients/apiClient'
import { PlanBadge } from '@/components/common/StatusBadge'

function KpiCard({
  label,
  value,
  icon,
  color = 'text-text-light dark:text-text-dark',
  sub,
  linkTo,
}: {
  label: string
  value: string | number
  icon: string
  color?: string
  sub?: string
  linkTo?: string
}) {
  const content = (
    <div className="h-full bg-surface-light dark:bg-surface-dark p-6 rounded-lg shadow-sm border border-border-light dark:border-border-dark hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-subtle-light dark:text-subtle-dark">{label}</p>
          <p className={`text-3xl font-bold mt-1 ${color}`}>{value}</p>
          {sub && <p className="text-xs text-subtle-light dark:text-subtle-dark mt-1">{sub}</p>}
        </div>
        <span className={`material-icons text-3xl ${color} opacity-80`}>{icon}</span>
      </div>
    </div>
  )

  if (linkTo) {
    return <Link to={linkTo} className="block h-full">{content}</Link>
  }
  return content
}

function currentPeriod() {
  return new Date().toISOString().slice(0, 7)
}

export default function Dashboard() {
  const { orgId } = useAuthStore()

  const { data: orgResp, isLoading: loadingOrg } = useQuery({
    queryKey: ['org', orgId],
    queryFn: () => orgApi.get(orgId!),
    enabled: !!orgId,
  })

  const { data: usersResp, isLoading: loadingUsers } = useQuery({
    queryKey: ['users', orgId],
    queryFn: () => usersApi.list(orgId!),
    enabled: !!orgId,
  })

  const { data: keysResp, isLoading: loadingKeys } = useQuery({
    queryKey: ['api-keys', orgId],
    queryFn: () => apiKeysApi.list(orgId!),
    enabled: !!orgId,
  })

  const { data: usageResp, isLoading: loadingUsage } = useQuery({
    queryKey: ['usage', orgId, currentPeriod()],
    queryFn: () => billingApi.getUsage(orgId!, currentPeriod()),
    enabled: !!orgId,
  })

  const isLoading = loadingOrg || loadingUsers || loadingKeys || loadingUsage

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <span className="material-icons animate-spin text-primary text-4xl">sync</span>
      </div>
    )
  }

  const org = orgResp?.data
  const users = usersResp?.data ?? []
  const keys = keysResp?.data ?? []
  const usage = usageResp?.data

  const costFormatted = usage?.CostUSD != null ? `$${usage.CostUSD.toFixed(4)}` : '$0.00'
  const period = currentPeriod()

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold text-text-light dark:text-text-dark">Dashboard</h1>
        <p className="text-sm text-subtle-light dark:text-subtle-dark mt-0.5">
          Account overview for {org?.name ?? '…'}
        </p>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-surface-light dark:bg-surface-dark p-6 rounded-lg shadow-sm border border-border-light dark:border-border-dark">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-subtle-light dark:text-subtle-dark">Organisation</p>
              <p className="text-xl font-bold mt-1 text-text-light dark:text-text-dark truncate max-w-[160px]">
                {org?.name ?? '—'}
              </p>
              {org?.plan && (
                <div className="mt-1">
                  <PlanBadge plan={org.plan} />
                </div>
              )}
            </div>
            <span className="material-icons text-3xl text-text-light dark:text-text-dark opacity-80">
              corporate_fare
            </span>
          </div>
        </div>

        <KpiCard
          label="Team Members"
          value={users.length}
          icon="group"
          sub={`${users.filter((u) => u.role === 'owner').length} owner(s)`}
          linkTo="/team"
        />

        <KpiCard
          label="Active API Keys"
          value={keys.length}
          icon="vpn_key"
          linkTo="/api-keys"
        />

        <KpiCard
          label={`Cost (${period})`}
          value={costFormatted}
          icon="payments"
          sub={
            usage
              ? `${(usage.InputTokens + usage.OutputTokens).toLocaleString()} total tokens`
              : undefined
          }
          linkTo="/usage"
          color={
            (usage?.CostUSD ?? 0) > 0
              ? 'text-warning'
              : 'text-text-light dark:text-text-dark'
          }
        />
      </div>

      {/* Usage summary card */}
      <div className="bg-surface-light dark:bg-surface-dark rounded-lg shadow-sm border border-border-light dark:border-border-dark p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-text-light dark:text-text-dark">
            LLM Usage — {period}
          </h2>
          <Link to="/usage" className="text-sm text-primary hover:underline">
            View details
          </Link>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <p className="text-xs text-subtle-light dark:text-subtle-dark uppercase tracking-wider">
              Input tokens
            </p>
            <p className="text-lg font-semibold text-text-light dark:text-text-dark mt-1">
              {(usage?.InputTokens ?? 0).toLocaleString()}
            </p>
          </div>
          <div>
            <p className="text-xs text-subtle-light dark:text-subtle-dark uppercase tracking-wider">
              Output tokens
            </p>
            <p className="text-lg font-semibold text-text-light dark:text-text-dark mt-1">
              {(usage?.OutputTokens ?? 0).toLocaleString()}
            </p>
          </div>
          <div>
            <p className="text-xs text-subtle-light dark:text-subtle-dark uppercase tracking-wider">
              Total cost
            </p>
            <p className="text-lg font-semibold text-warning mt-1">{costFormatted}</p>
          </div>
        </div>
      </div>

      {/* Quick links */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[
          { to: '/team', icon: 'group_add', title: 'Manage Team', desc: 'View and update member roles' },
          { to: '/api-keys', icon: 'add_circle', title: 'Issue API Key', desc: 'Create a new API key for integrations' },
          { to: '/budget', icon: 'tune', title: 'Budget Settings', desc: 'Set spending limits and alerts' },
        ].map((item) => (
          <Link
            key={item.to}
            to={item.to}
            className="flex items-start gap-3 p-4 bg-surface-light dark:bg-surface-dark rounded-lg border border-border-light dark:border-border-dark hover:border-primary/50 hover:shadow-sm transition-all"
          >
            <span className="material-icons text-primary text-2xl">{item.icon}</span>
            <div>
              <p className="text-sm font-medium text-text-light dark:text-text-dark">{item.title}</p>
              <p className="text-xs text-subtle-light dark:text-subtle-dark mt-0.5">{item.desc}</p>
            </div>
          </Link>
        ))}
      </div>
    </div>
  )
}
